// src/hooks/useBrowserNotifications.ts
import { useEffect, useRef } from 'react';

export function useBrowserNotifications() {
  const originalTitleRef = useRef<string>(document.title);
  const flashIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isFlashingRef = useRef(false);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const flashTitle = (message: string) => {
    if (isFlashingRef.current) return;
    
    isFlashingRef.current = true;
    let isOriginal = true;

    flashIntervalRef.current = setInterval(() => {
      document.title = isOriginal ? message : originalTitleRef.current;
      isOriginal = !isOriginal;
    }, 1000);

    // Stop flashing when user returns to tab
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        stopFlashing();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };

  const stopFlashing = () => {
    if (flashIntervalRef.current) {
      clearInterval(flashIntervalRef.current);
      flashIntervalRef.current = null;
    }
    document.title = originalTitleRef.current;
    isFlashingRef.current = false;
  };

  const showNotification = (title: string, options?: NotificationOptions) => {
    // Flash title if tab is not visible
    if (document.hidden) {
      flashTitle(`💬 ${title}`);
    }

    // Show browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });
    }
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      stopFlashing();
    };
  }, []);

  return { showNotification, flashTitle, stopFlashing };
}
